import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { Box, Typography, Button, TextField, CircularProgress, Alert, Chip } from '@mui/material'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { executeRecaptchaV3 } from 'src/views/applyPublic/recaptchaV3'
import { startValidation, verifyValidation, buildClientMeta } from './certificateValidationService'

const FRAME = {
  width: '100%',
  minHeight: '100svh',
  margin: 0,
  boxSizing: 'border-box',
  padding: '24px 16px',
  background: '#f5f7fb',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  fontFamily: 'Outfit, Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  color: '#111827',
}

const CARD = {
  width: '100%',
  maxWidth: 460,
  // box-sizing + px-literal padding/radius: these are spread into a MUI `sx`, where a bare
  // number means theme-spacing units (24 → 192px!). On public routes that load CoreUI/Bootstrap
  // (which sets box-sizing: border-box globally), that 192px padding collapsed the card content
  // to a sliver. Pin px + border-box so the layout is correct regardless of which global CSS is live.
  boxSizing: 'border-box',
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
}

const isOk = (resp) => resp?.code === 2000

export default function PublicCertificateValidationPage() {
  const { certId } = useParams()
  const [step, setStep] = useState('mobile') // mobile | otp | result | restricted | invalid
  const [mobile, setMobile] = useState('')
  const [viewerName, setViewerName] = useState('')
  const [sessionId, setSessionId] = useState(null)
  const [otp, setOtp] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [info, setInfo] = useState(null)
  const [cooldown, setCooldown] = useState(0)
  const [result, setResult] = useState(null) // { certificate, pdfUrl } | { message }
  const cooldownRef = useRef(null)

  useEffect(() => () => clearInterval(cooldownRef.current), [])

  const startCooldown = (secs) => {
    const s = Number(secs) || 0
    if (s <= 0) return
    setCooldown(s)
    clearInterval(cooldownRef.current)
    cooldownRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          clearInterval(cooldownRef.current)
          return 0
        }
        return c - 1
      })
    }, 1000)
  }

  const handleStart = async () => {
    setError(null)
    setInfo(null)
    const digits = (mobile || '').replace(/\D/g, '')
    if (digits.length < 8) {
      setError('Please enter a valid mobile number.')
      return
    }
    setBusy(true)
    try {
      const token = await executeRecaptchaV3('cert_validation_otp_send')
      const resp = await startValidation(certId, {
        mobile: digits,
        viewerName,
        recaptchaToken: token,
        clientMeta: buildClientMeta(),
      })
      if (!isOk(resp)) {
        setError(resp?.message || 'Could not continue. Please try again.')
        return
      }
      const data = resp.data || {}
      if (data.open) {
        // OPEN policy — no OTP required; the document is returned directly.
        setResult({ certificate: data.certificate, pdfUrl: data.pdfUrl })
        setStep('result')
      } else {
        setSessionId(data.sessionId)
        setStep('otp')
        setInfo('We sent a one-time code to your WhatsApp.')
        startCooldown(data.retryAfterSeconds)
      }
    } catch (e) {
      setError('Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const handleVerify = async () => {
    setError(null)
    if (!otp || otp.trim().length < 4) {
      setError('Enter the code you received.')
      return
    }
    setBusy(true)
    try {
      const token = await executeRecaptchaV3('cert_validation_otp_verify')
      const resp = await verifyValidation(certId, {
        sessionId,
        otp: otp.trim(),
        viewerName,
        recaptchaToken: token,
        clientMeta: buildClientMeta(),
      })
      if (!isOk(resp)) {
        setError(resp?.message || 'Incorrect or expired code.')
        return
      }
      const data = resp.data || {}
      if (data.valid === false) {
        setResult({ message: data.message || 'This certificate is not valid.' })
        setStep('invalid')
      } else if (data.allowed === false) {
        setResult({ message: data.message })
        setStep('restricted')
      } else {
        setResult({ certificate: data.certificate, pdfUrl: data.pdfUrl })
        setStep('result')
      }
    } catch (e) {
      setError('Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main style={FRAME}>
      <Box sx={{ ...CARD }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <VerifiedUserIcon sx={{ color: '#1F4E79' }} />
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Verify medical document</Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {info && step === 'otp' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {info}
          </Alert>
        )}

        {step === 'mobile' && (
          <>
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2 }}>
              Enter your details to view this document. Depending on the issuer&apos;s settings you may be asked
              for a one-time code on WhatsApp. Your access is logged.
            </Typography>
            <TextField
              label="Your name (optional)"
              value={viewerName}
              onChange={(e) => setViewerName(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 1.5 }}
            />
            <PhoneInput
              country={'in'}
              value={mobile}
              onChange={(v) => setMobile(v)}
              inputStyle={{ width: '100%' }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleStart}
              disabled={busy}
              sx={{ mt: 2, textTransform: 'none' }}
            >
              {busy ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
            </Button>
          </>
        )}

        {step === 'otp' && (
          <>
            <TextField
              label="Enter the code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              fullWidth
              inputProps={{ inputMode: 'numeric', maxLength: 8 }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleVerify}
              disabled={busy}
              sx={{ mt: 2, textTransform: 'none' }}
            >
              {busy ? <CircularProgress size={20} color="inherit" /> : 'Verify & view'}
            </Button>
            <Button
              fullWidth
              onClick={handleStart}
              disabled={busy || cooldown > 0}
              sx={{ mt: 1, textTransform: 'none' }}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
            </Button>
          </>
        )}

        {step === 'restricted' && (
          <Alert severity="warning">{result?.message}</Alert>
        )}

        {step === 'invalid' && <Alert severity="error">{result?.message}</Alert>}

        {step === 'result' && result?.certificate && (
          <Box>
            <Alert icon={<VerifiedUserIcon fontSize="inherit" />} severity="success" sx={{ mb: 2 }}>
              Verified authentic document.
            </Alert>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 0.5, columnGap: 1.5, mb: 2, fontSize: 13.5 }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Document</Typography>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{result.certificate.title || '—'}</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Issued by</Typography>
              <Typography sx={{ fontSize: 13.5 }}>{result.certificate.issuerName || '—'}</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Patient</Typography>
              <Typography sx={{ fontSize: 13.5 }}>{result.certificate.patientName || '—'}</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Issued on</Typography>
              <Typography sx={{ fontSize: 13.5 }}>{result.certificate.issuedOn || '—'}</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Reference</Typography>
              <Typography sx={{ fontSize: 13.5 }}>
                {result.certificate.certificateNumber || '—'}{' '}
                <Chip size="small" color="success" variant="outlined" label={result.certificate.status} />
              </Typography>
            </Box>
            {result.pdfUrl ? (
              <iframe
                title="Certificate"
                src={result.pdfUrl}
                style={{ width: '100%', height: '60vh', border: '1px solid #e0e0e0', borderRadius: 8 }}
              />
            ) : (
              <Typography color="text.secondary">The document could not be loaded.</Typography>
            )}
          </Box>
        )}

        <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 3, textAlign: 'center' }}>
          Protected by reCAPTCHA. This verification is logged.
        </Typography>
      </Box>
    </main>
  )
}
